/**
 * Наличие рекомендованных мероприятий (форма №6).
 *
 * Хранится в карточке, в поле form6.activities_required:
 *   YES  — «Да»: мероприятия есть, таблица мероприятий раскрыта;
 *   NO   — «Нет»;
 *   NONE — «Не выбрано»: на форме висит подсказка, что наличие не указано.
 * На экране то же значение живёт как true / false / null — так его удобнее
 * отдавать селектору; здесь же лежат переводы в поле карточки и обратно.
 *
 * Когда значения ещё нет — новая карточка, пока селектор не трогали, — оно
 * выводится по самим мероприятиям: заполнены — «Да», иначе «Не выбрано».
 * Выбор, сделанный руками, эту автоматику перебивает.
 *
 * Подсказка показывается на самой форме №6, рядом с полем. Без выбора карточку
 * не отправить на согласование (так требует мобильное приложение и проверяет сервер),
 * поэтому в панели «Замечания» есть и своя строка — см. utils/cardValidation.js.
 */

export const RECOMMENDED_MEASURES_NOTICE = 'Наличие рекомендованных мероприятий не указано';

/** Значения поля form6.activities_required. */
export const ACTIVITIES_REQUIRED = Object.freeze({ YES: 'YES', NO: 'NO', NONE: 'NONE' });

/** Значение селектора (true / false / null) → значение поля карточки. */
export const toActivitiesRequired = (value) => {
  if (value === true) {
    return ACTIVITIES_REQUIRED.YES;
  }
  if (value === false) {
    return ACTIVITIES_REQUIRED.NO;
  }
  return ACTIVITIES_REQUIRED.NONE;
};

/**
 * Значение поля карточки → значение селектора.
 * undefined — поля в карточке нет: она сохранена до его появления или приложением,
 * которое про него не знает.
 */
export const fromActivitiesRequired = (code) => {
  const normalized = String(code === null || code === undefined ? '' : code).trim().toUpperCase();
  if (normalized === ACTIVITIES_REQUIRED.YES) {
    return true;
  }
  if (normalized === ACTIVITIES_REQUIRED.NO) {
    return false;
  }
  if (normalized === ACTIVITIES_REQUIRED.NONE) {
    return null;
  }
  return undefined;
};

const filled = (value) => String(value === null || value === undefined ? '' : value).trim() !== '';

// Площадь в пустой строке мероприятия отображается как «0.0000» — это не заполнение.
const filledNumber = (value) => {
  const number = parseFloat(String(value === null || value === undefined ? '' : value).replace(',', '.'));
  return Number.isFinite(number) && number !== 0;
};

/** В карточке есть хоть одна непустая строка мероприятий. */
export const hasFilledRecommendedMeasures = (form6State) => (
  (form6State?.recommended_measures || []).some(row => row && (
    !!row.rekvidmer || filled(row.srokmzl) || filledNumber(row.srekme) || filled(row.primechmzl)
  ))
);

/** true — «Да», false — «Нет», null — «Не выбрано». */
export const resolveHasRecommendedMeasures = ({ form6State, isNewCard = false } = {}) => {
  const chosen = form6State?.has_recommended_measures;
  if (chosen === true || chosen === false || chosen === null) {
    return chosen;
  }
  if (hasFilledRecommendedMeasures(form6State)) {
    return true;
  }
  // У открытой карточки значение всегда проставляет загрузчик, так что сюда
  // попадает только новая. isNewCard — сессия создания из App: по id её не
  // отличить, при первом автосохранении новая карточка получает UUID.
  return isNewCard ? null : false;
};

/**
 * Что записать в поле карточки при сохранении. Явный выбор — как есть; если
 * селектор не трогали (так бывает только у новой карточки), решают сами
 * мероприятия: заполнены — «Да», иначе «Не выбрано».
 */
export const activitiesRequiredForSave = (form6State) => {
  const chosen = form6State?.has_recommended_measures;
  if (chosen === true || chosen === false || chosen === null) {
    return toActivitiesRequired(chosen);
  }
  return toActivitiesRequired(hasFilledRecommendedMeasures(form6State) ? true : null);
};

/**
 * Значение селектора для загруженной карточки. Если поле сохранено — берём его.
 * Если нет — выводим так же, как раньше для открытой карточки: по мероприятиям.
 */
export const hasRecommendedMeasuresFromCard = (activitiesRequired, recommendedMeasures) => {
  const saved = fromActivitiesRequired(activitiesRequired);
  if (saved !== undefined) {
    return saved;
  }
  return hasFilledRecommendedMeasures({ recommended_measures: recommendedMeasures });
};
